<template>
  <div class="c-virtual-list__row">
    <div class="c-virtual-list__col check" @click.prevent="click">
      <Checkbox
        :id="'keyword_' + item.id"
        name="keywords[]"
        :value="item.id"
        :checked="checked"
        ref="checkbox"
        @change="change"
      />
    </div>
    <div class="c-virtual-list__col keyword">
      <EllipsisPopover placement="right-end">
        {{ item.keyword }}
      </EllipsisPopover>
    </div>
    <div class="c-virtual-list__col title">
      <EllipsisPopover :clamp="1" placement="right-end">
        <a :href="item.url" target="_blank" class="c-link--blank" @click.stop>
          {{ item.title ? item.title : item.url }}
        </a>
        <template slot="popover">
          <a :href="item.url" target="_blank" @click.stop>
            {{ item.title ? item.title : item.url }}
          </a>
        </template>
      </EllipsisPopover>
      <p v-if="!item.title" class="c-text--note">
        タイトルが表示されない原因は<a
          href="https://support.searchwrite.jp/hc/ja/articles/360004428195"
          target="_blank"
          >こちら</a
        >
      </p>
    </div>
    <div class="c-virtual-list__col task">
      <div class="c-list--category">
        <p :class="categoryCssClass">
          {{ category }}
        </p>
      </div>
    </div>
    <div class="c-virtual-list__col doneDate">
      {{ item.doneDate | dateText('YYYY.MM.DD') }}
    </div>
    <div class="c-virtual-list__col rankDiff c-text--right">
      <span :class="rankDiffClass">{{ rankDiffText }}</span>
    </div>
    <div class="c-virtual-list__col doneRank c-text--right">
      {{ doneTaskRankText }}
    </div>
    <div class="c-virtual-list__col currentRank c-text--right">
      {{ currentRankText }}
    </div>
    <div class="c-virtual-list__col clicks c-text--right">
      {{ summariesText.clicks }}
    </div>
    <div class="c-virtual-list__col searchVolume c-text--right">
      {{ summariesText.searchVolumes }}
    </div>
    <div class="c-virtual-list__col impressions c-text--right">
      <Loading v-if="isLoading" variant="inline" />
      {{ summariesText.impressions }}
    </div>
    <div class="c-virtual-list__col ctr c-text--right">
      {{ summariesText.ctr }}
    </div>
    <div class="c-virtual-list__col cv c-text--right">
      {{ summariesText.conversions }}
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/Checkbox';
import EllipsisPopover from '@/components/EllipsisPopover';
import Loading from '@/components/Loading';
import utils from '@/utils';

export default {
  props: {
    checked: {
      type: Array,
      required: true
    },
    item: {
      type: Object,
      required: true
    }
  },
  components: {
    Checkbox,
    EllipsisPopover,
    Loading
  },
  computed: {
    category() {
      let category = '';

      if (this.categoryLabel[this.item.type]) {
        if (this.item.isProposal) {
          category += '提案 - ';
        }
        category += this.categoryLabel[this.item.type];
      }
      return category;
    },
    categoryCssClass() {
      return {
        'c-list--category__item': true,
        ['c-list--category__item--' + this.item.type]: this.item.type
      };
    },
    categoryLabel() {
      return this.$store.getters['task/solutionLabel'];
    },
    currentRankText() {
      if (this.item.currentRank || this.item.currentRank === 0) {
        return utils.roundFloatingPointNumber(this.item.currentRank, 1);
      }
      return '-';
    },
    doneTaskRankText() {
      // 新規コンテンツ作成タスクの場合は、順位があってもなくても-を表示する
      if (
        !this.isCreateContentTask &&
        (this.item.doneTaskRank || this.item.doneTaskRank === 0)
      ) {
        return utils.roundFloatingPointNumber(this.item.doneTaskRank, 1);
      }
      return '-';
    },
    isCreateContentTask() {
      return this.item.type === 'createContent';
    },
    isLoading() {
      return !this.item || this.item.isLoading;
    },
    rankDiff() {
      if (
        !this.isCreateContentTask &&
        (this.item.doneTaskRank || this.item.doneTaskRank === 0) &&
        (this.item.currentRank || this.item.currentRank === 0)
      ) {
        // 数値を小数点第二位で四捨五入してから差分を計算する
        // そうしないと画面上は同じ順位でも、順位変動の数値が0.0にならない場合がある
        const doneTaskRank = utils.roundFloatingPointNumber(
          this.item.doneTaskRank,
          1
        );
        const currentRank = utils.roundFloatingPointNumber(
          this.item.currentRank,
          1
        );
        return doneTaskRank - currentRank;
      }
      return null;
    },
    rankDiffText() {
      if (this.rankDiff !== null) {
        const sign = this.rankDiff >= 0 ? '+' : '-';
        const roundDiff = Math.abs(this.rankDiff).toLocaleString('ja-JP', {
          minimumFractionDigits: 1 //小数点第一位を必ず表示する
        });
        return sign + roundDiff;
      }
      return '-';
    },
    rankDiffClass() {
      // 0の時はクラスを付けない
      if (this.rankDiff) {
        return this.rankDiff >= 0 ? 'positive' : 'negative';
      }
      return '';
    },
    summariesText() {
      if (this.isLoading || this.item.status !== 200) {
        return {};
      }
      const clicks = this.$options.filters.separateNumber(this.item.clicks);
      const impressions = this.$options.filters.separateNumber(
        this.item.impressions
      );
      const searchVolumes = this.$options.filters.separateNumber(
        this.item.searchVolumes
      );
      const ctr =
        this.$options.filters.roundFloatingPointNumber(this.item.ctr * 100, 2) +
        '%';
      const conversions = this.$options.filters.separateNumber(
        utils.roundFloatingPointNumber(this.item.conversions, 2)
      );

      return { clicks, impressions, searchVolumes, ctr, conversions };
    }
  },
  methods: {
    change(checked) {
      this.$emit('check', checked);
    },
    click() {
      this.$refs.checkbox.change();
    }
  }
};
</script>
